const raveyardReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

const raveyardMotionIsEnabled = () =>
  !raveyardReducedMotion.matches && !document.body.classList.contains("ry-motion-off");

const syncRaveyardMotionVideo = (video) => {
  const hiddenSlide = video.closest('[data-ry-gallery-slide][aria-hidden="true"]');
  const inViewport = video.dataset.ryInViewport !== "false";
  const shouldPlay = raveyardMotionIsEnabled() && inViewport && !hiddenSlide;

  if (shouldPlay) {
    video.play().catch(() => {});
    return;
  }

  video.pause();
  if (!raveyardMotionIsEnabled() && video.readyState > 0) {
    try {
      video.currentTime = 0;
    } catch (_) {
      // Some streaming sources are not seekable until more metadata is available.
    }
  }
};

const raveyardMotionObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      entry.target.querySelectorAll("video").forEach((video) => {
        video.dataset.ryInViewport = entry.isIntersecting ? "true" : "false";
        syncRaveyardMotionVideo(video);
      });
    });
  },
  { rootMargin: "160px 0px", threshold: 0.08 }
);

const initRaveyardMotionVideos = (root = document) => {
  root.querySelectorAll("[data-ry-motion-media]").forEach((media) => {
    if (media.dataset.ryMotionInitialized === "true") return;
    media.dataset.ryMotionInitialized = "true";
    media.querySelectorAll("video").forEach((video) => {
      video.autoplay = false;
      video.muted = true;
    });
    raveyardMotionObserver.observe(media);
  });
};

class RaveyardProductGallery extends HTMLElement {
  connectedCallback() {
    window.requestAnimationFrame(() => this.init());
  }

  init() {
    this.index = 0;
    this.slides = [...this.querySelectorAll("[data-ry-gallery-slide]")];
    this.buttons = [...this.querySelectorAll("[data-ry-gallery-dir]")];
    this.counter = this.querySelector("[data-ry-gallery-counter]");
    if (!this.slides.length) return;
    if (this.initialized) return;
    this.initialized = true;

    this.buttons.forEach((button) => {
      button.addEventListener("click", () => {
        this.show(this.index + Number(button.dataset.ryGalleryDir || 1));
      });
    });

    this.show(0);
  }

  show(next) {
    this.index = (next + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, index) => {
      const active = index === this.index;
      slide.classList.toggle("is-active", active);
      slide.setAttribute("aria-hidden", active ? "false" : "true");
      slide.querySelectorAll("video").forEach((video) => {
        if (!active) video.pause();
        else syncRaveyardMotionVideo(video);
      });
    });

    if (this.counter) {
      this.counter.textContent = `${this.index + 1} / ${this.slides.length}`;
    }
  }
}

if (!customElements.get("raveyard-product-gallery")) {
  customElements.define("raveyard-product-gallery", RaveyardProductGallery);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initRaveyardMotionVideos());
} else {
  initRaveyardMotionVideos();
}

document.addEventListener("shopify:section:load", (event) => initRaveyardMotionVideos(event.target));

const syncAllRaveyardMotionVideos = () => {
  document.querySelectorAll("[data-ry-motion-media] video").forEach(syncRaveyardMotionVideo);
};

if (raveyardReducedMotion.addEventListener) {
  raveyardReducedMotion.addEventListener("change", syncAllRaveyardMotionVideos);
} else {
  raveyardReducedMotion.addListener(syncAllRaveyardMotionVideos);
}

class RaveyardTestimonials extends HTMLElement {
  connectedCallback() {
    window.requestAnimationFrame(() => this.init());
  }

  disconnectedCallback() {
    this.pause();
  }

  init() {
    this.index = 0;
    this.slides = [...this.querySelectorAll("[data-ry-slide]")];
    this.dots = [...this.querySelectorAll("[data-ry-dot]")];
    this.buttons = [...this.querySelectorAll("[data-ry-dir]")];
    if (!this.slides.length) {
      window.setTimeout(() => this.init(), 100);
      return;
    }
    if (this.initialized) return;
    this.initialized = true;

    this.buttons.forEach((button) => {
      button.addEventListener("click", () => {
        this.show(this.index + Number(button.dataset.ryDir || 1));
        this.restart();
      });
    });

    this.dots.forEach((dot, index) => {
      dot.addEventListener("click", () => {
        this.show(index);
        this.restart();
      });
    });

    this.show(0);
    this.restart();
    this.addEventListener("mouseenter", () => this.pause());
    this.addEventListener("focusin", () => this.pause());
    this.addEventListener("mouseleave", () => this.restart());
    this.addEventListener("focusout", () => this.restart());
  }

  pause() {
    window.clearInterval(this.timer);
  }

  restart() {
    this.pause();
    if (this.slides.length > 1) {
      this.timer = window.setInterval(() => this.show(this.index + 1), 7000);
    }
  }

  show(next) {
    this.index = (next + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, index) => slide.classList.toggle("is-active", index === this.index));
    this.dots.forEach((dot, index) => dot.classList.toggle("is-active", index === this.index));
  }
}

if (!customElements.get("raveyard-testimonials")) {
  customElements.define("raveyard-testimonials", RaveyardTestimonials);
}

class RaveyardReviewCarousel extends HTMLElement {
  connectedCallback() {
    window.requestAnimationFrame(() => this.init());
  }

  disconnectedCallback() {
    this.pause();
  }

  init() {
    this.index = 1;
    this.slides = [...this.querySelectorAll("[data-ry-review-slide]")];
    this.dots = [...this.querySelectorAll("[data-ry-review-dot]")];
    this.buttons = [...this.querySelectorAll("[data-ry-review-dir]")];
    if (!this.slides.length) {
      window.setTimeout(() => this.init(), 100);
      return;
    }
    if (this.initialized) return;
    this.initialized = true;

    if (this.slides.length < 3) {
      this.index = 0;
    }

    this.buttons.forEach((button) => {
      button.addEventListener("click", () => {
        const direction = Number(button.dataset.ryReviewDir || 1);
        this.move(this.index + direction, direction);
        this.restart();
      });
    });

    this.dots.forEach((dot, index) => {
      dot.addEventListener("click", () => {
        const direction = index >= this.index ? 1 : -1;
        this.move(index, direction);
        this.restart();
      });
    });

    this.addEventListener("mouseenter", () => this.pause());
    this.addEventListener("focusin", () => this.pause());
    this.addEventListener("mouseleave", () => this.restart());
    this.addEventListener("focusout", () => this.restart());

    this.show(this.index);
    this.restart();
  }

  pause() {
    window.clearInterval(this.timer);
  }

  restart() {
    this.pause();
    if (this.slides.length > 1) {
      this.timer = window.setInterval(() => this.move(this.index + 1, 1), 7000);
    }
  }

  move(next, direction = 1) {
    const normalizedDirection = direction < 0 ? -1 : 1;
    const oldPrev = (this.index - 1 + this.slides.length) % this.slides.length;
    const oldNext = (this.index + 1) % this.slides.length;
    this.dataset.ryDirection = normalizedDirection < 0 ? "prev" : "next";
    this.show(next, { direction: normalizedDirection, oldPrev, oldNext });
  }

  show(next, motion = {}) {
    this.index = (next + this.slides.length) % this.slides.length;
    const prev = (this.index - 1 + this.slides.length) % this.slides.length;
    const following = (this.index + 1) % this.slides.length;

    this.slides.forEach((slide, index) => {
      const isPrev = index === prev && this.slides.length > 1;
      const isActive = index === this.index;
      const isNext = index === following && this.slides.length > 2;
      const isVisible = isPrev || isActive || isNext;

      slide.classList.toggle("is-prev", isPrev);
      slide.classList.toggle("is-active", isActive);
      slide.classList.toggle("is-next", isNext);
      slide.classList.toggle("is-exiting-left", !isVisible && motion.direction > 0 && index === motion.oldPrev);
      slide.classList.toggle("is-exiting-right", !isVisible && motion.direction < 0 && index === motion.oldNext);
    });

    this.dots.forEach((dot, index) => {
      dot.classList.toggle("is-active", index === this.index);
    });

    window.clearTimeout(this.exitTimer);
    this.exitTimer = window.setTimeout(() => {
      this.slides.forEach((slide) => {
        slide.classList.remove("is-exiting-left", "is-exiting-right");
      });
    }, 620);
  }
}

if (!customElements.get("raveyard-review-carousel")) {
  customElements.define("raveyard-review-carousel", RaveyardReviewCarousel);
}

class RaveyardPackCarousel extends HTMLElement {
  connectedCallback() {
    window.requestAnimationFrame(() => this.init());
  }

  init() {
    this.track = this.querySelector("[data-ry-pack-track]");
    this.cards = [...this.querySelectorAll("[data-ry-pack-card]")];
    this.buttons = [...this.querySelectorAll("[data-ry-pack-dir]")];
    this.counter = this.querySelector("[data-ry-pack-counter]");
    if (!this.track || !this.cards.length) {
      window.setTimeout(() => this.init(), 100);
      return;
    }
    if (this.initialized) return;
    this.initialized = true;
    this.isStatic = this.cards.length <= 4;
    this.classList.toggle("is-static", this.isStatic);

    this.buttons.forEach((button) => {
      button.addEventListener("click", () => {
        if (this.isStatic) return;
        this.move(Number(button.dataset.ryPackDir || 1));
      });
    });

    this.track.addEventListener("scroll", () => this.update(), { passive: true });
    window.addEventListener("resize", () => this.update());
    this.update();
  }

  move(direction) {
    const current = this.currentIndex();
    const next = Math.max(0, Math.min(this.cards.length - 1, current + direction));
    this.cards[next].scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
  }

  currentIndex() {
    const trackLeft = this.track.getBoundingClientRect().left;
    let closestIndex = 0;
    let closestDistance = Infinity;

    this.cards.forEach((card, index) => {
      const distance = Math.abs(card.getBoundingClientRect().left - trackLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  update() {
    const index = this.currentIndex();
    if (this.counter) {
      this.counter.textContent = `${index + 1} of ${this.cards.length}`;
    }

    if (this.isStatic) {
      this.buttons.forEach((button) => {
        button.disabled = true;
      });
      return;
    }

    this.buttons.forEach((button) => {
      const direction = Number(button.dataset.ryPackDir || 1);
      button.disabled = direction < 0 ? index === 0 : index >= this.cards.length - 1;
    });
  }
}

if (!customElements.get("raveyard-pack-carousel")) {
  customElements.define("raveyard-pack-carousel", RaveyardPackCarousel);
}

const fitRaveyardHeadings = () => {
  const headings = document.querySelectorAll(".ry-pdp .ry-section h2, .ry-pdp .ry-cta-grid h2");
  headings.forEach((heading) => {
    heading.style.removeProperty("font-size");

    const initialStyles = window.getComputedStyle(heading);
    let size = Number.parseFloat(initialStyles.fontSize);
    if (!Number.isFinite(size) || heading.getBoundingClientRect().width === 0) return;

    const minSize = window.matchMedia("(max-width: 749px)").matches ? 34 : 40;
    const lineCount = () => {
      const styles = window.getComputedStyle(heading);
      const lineHeight = Number.parseFloat(styles.lineHeight) || size * 0.95;
      return Math.ceil(heading.getBoundingClientRect().height / lineHeight);
    };

    heading.style.fontSize = `${size}px`;

    let attempts = 0;
    while (lineCount() > 3 && size > minSize && attempts < 28) {
      size -= 2;
      heading.style.fontSize = `${size}px`;
      attempts += 1;
    }
  });
};

const scheduleRaveyardHeadingFit = () => window.requestAnimationFrame(fitRaveyardHeadings);

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", scheduleRaveyardHeadingFit);
} else {
  scheduleRaveyardHeadingFit();
}

window.addEventListener("load", scheduleRaveyardHeadingFit);
window.addEventListener("resize", scheduleRaveyardHeadingFit);
document.addEventListener("shopify:section:load", scheduleRaveyardHeadingFit);

if (document.fonts && document.fonts.ready) {
  document.fonts.ready.then(scheduleRaveyardHeadingFit);
}
